import * as React from 'react';
import { FC, useState, useEffect, useCallback, useMemo } from 'react';
import { ListGroup, ListGroupItem, Modal, ModalHeader, ModalBody, ModalFooter, Badge, Alert } from 'reactstrap';
import { CircularProgress } from '@material-ui/core';
import styled from 'styled-components';
import { useTranslations } from '../../../store/translations/hooks';
import { TK } from '../../../store/translations/translationKeys';
import { createSurveyAsync, updateSurveyAsync, deleteSurveyAsync, getSurveyPreviewLinkAsync, generateSurveyAsync, getSurveyShareLinkAsync, sendSurveyToContactAsync } from '../../../ajax/requests';
import { getErrorMessage } from '../../../ajax/errors';
import { useDispatch, useSelector } from 'react-redux';
import { alertError, alertSuccess } from '../../../store/alerts/actions';
import { IconButton } from '@material-ui/core';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import VisibilityIcon from '@material-ui/icons/Visibility';
import BarChartIcon from '@material-ui/icons/BarChart';
import EmailIcon from '@material-ui/icons/Email';
import WhatsAppIcon from '@material-ui/icons/WhatsApp';
import ShareIcon from '@material-ui/icons/Share';
import SparkleAIIcon from '../../../components/Icons/SparkleAIIcon';
import { paths } from '../../../app/constants';
import {
    SurveyCard,
    SurveyMeta,
    SurveyName,
    SurveyDate,
    SurveyFieldBadges,
    SurveyActions,
    FieldRow,
    FieldRowHeader,
    FieldRowTitle,
    ScaleRangeRow,
    EmptyState,
    AddSurveyRow,
    SurveyStats,
    SurveyStatChip,
    ChannelCard,
    ChannelHeader,
    ChannelInfo,
    ChannelContent,
    ChannelStatus,
    AISection,
    GeneratingOverlay,
    GeneratingLabel,
    ShareIconWrapper,
    ShareLinkRow,
    ShareLinkText,
    ShareSectionDivider,
    ShareSendHeader,
    ShareSendTitle,
} from './SurveysSettings.styles';
import { push } from 'connected-react-router';
import {
    SurveyDraftData,
    SurveyFieldDefinitionData,
    SurveyFieldType,
    SurveySettingsData,
    SurveyUpdateRequest,
} from '../../../shared/surveys';
import { AutocompleteTextField, CheckInputField, ComboField, NumberInputField, TextInputField } from '../../../components/Forms/fields';
import CollapsableItem from '../../../components/CollapsableItem';
import SurveyDescriptionEditor from './SurveyDescriptionEditor';
import { useGeneralSettings, useAIPoweredRequests } from '../../../store/settings/hooks';
import { ApplicationState } from '../../../store';
import { loadSurveys as loadSurveysIfNeeded, refreshSurveys } from '../../../store/settings/actions';
import { getSurveys, isSurveysLoading } from '../../../store/settings/selectors';
import { getEntitiesDataItems } from '../../../store/entities/selectors';
import { ClientEntity } from '../../../store/entities/state';
import { createClientSearchConfig } from '../../../shared/clientSearch';
import AppButton from '../../../components/AppButton';

interface CreateButtonProps {
    $ai?: boolean;
    id?: string;
    onClick?: () => void;
    children?: React.ReactNode;
}

const CreateButton = styled.button<CreateButtonProps>`
    display: inline-flex;
    align-items: center;
    gap: var(--spacer-XS);
    background: none;
    border: var(--border-thin) solid ${p => p.$ai ? 'var(--info-color)' : 'var(--appLigthGrey)'};
    border-radius: 4px;
    color: ${p => p.$ai ? 'var(--info-color)' : 'var(--text-body)'};
    cursor: pointer;
    font-size: 0.875rem;
    padding: var(--spacer-XXS) var(--spacer-S);
    font-weight: 500;
    transition: background 0.15s, border-color 0.15s, color 0.15s;

    &:hover {
        background: ${p => p.$ai ? 'rgba(102, 126, 234, 0.08)' : 'var(--bg-light)'};
        border-color: ${p => p.$ai ? 'var(--info-color)' : 'var(--text-muted)'};
    }
`;

const FIELD_TYPES = [
    { value: SurveyFieldType.SingleLineText, labelKey: TK.surveyFieldTypeSingleLineText },
    { value: SurveyFieldType.MultilineText, labelKey: TK.surveyFieldTypeMultilineText },
    { value: SurveyFieldType.IntNumber, labelKey: TK.surveyFieldTypeIntNumber },
    { value: SurveyFieldType.MultiOptions, labelKey: TK.surveyFieldTypeMultiOptions },
    { value: SurveyFieldType.Scale, labelKey: TK.surveyFieldTypeScale },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

const htmlToPlainText = (html: string): string => {
    return html
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/p>/gi, '\n\n')
        .replace(/<[^>]*>/g, '')
        .trim();
};

const plainTextToHtml = (text: string): string => {
    return text
        .trim()
        .split(/\n\n/)
        .map(para => `<p>${para.replace(/\n/g, '<br/>')}</p>`)
        .join('');
};

let _fieldIdCounter = 0;
const createEmptyField = (): SurveyFieldDefinitionData & { _key: number } => ({
    _key: ++_fieldIdCounter,
    label: '',
    type: SurveyFieldType.MultilineText,
    isMandatory: false,
    options: [],
    scaleMin: 1,
    scaleMax: 10,
});

const toDraftFields = (fields: SurveyFieldDefinitionData[]): Array<SurveyFieldDefinitionData & { _key: number }> =>
    fields.map(field => ({
        ...field,
        _key: ++_fieldIdCounter,
    }));

const stripDraftFields = (fields: Array<SurveyFieldDefinitionData & { _key: number }>): SurveyFieldDefinitionData[] =>
    fields.map(({ _key, ...field }) => field);

// ─── Field Editor ─────────────────────────────────────────────────────────────

interface FieldEditorProps {
    field: SurveyFieldDefinitionData;
    index: number;
    onUpdate: (index: number, updates: Partial<SurveyFieldDefinitionData>) => void;
    onRemove: (index: number) => void;
}

const FieldEditor: FC<FieldEditorProps> = ({ field, index, onUpdate, onRemove }) => {
    const t = useTranslations();
    const [optionsRaw, setOptionsRaw] = useState(() => (field.options ?? []).join(', '));

    useEffect(() => {
        setOptionsRaw((field.options ?? []).join(', '));
    }, [field.options]);

    const fieldTypeOptions = FIELD_TYPES.map(ft => ({ value: String(ft.value), label: t(ft.labelKey) }));

    const handleOptionsChange = useCallback((value: string) => {
        setOptionsRaw(value);
        onUpdate(index, { options: value.split(',').map(o => o.trim()).filter(Boolean) });
    }, [index, onUpdate]);

    return (
        <FieldRow>
            <FieldRowHeader>
                <FieldRowTitle>{t(TK.surveyField)} {index + 1}</FieldRowTitle>
                <AppButton
                    color="link"
                    size="sm"
                    onClick={() => onRemove(index)}
                    style={{ color: 'var(--danger-color)', padding: 0 }}
                >
                    {t(TK.remove)}
                </AppButton>
            </FieldRowHeader>
            <TextInputField
                label={t(TK.fieldLabel)}
                value={field.label}
                onChange={value => onUpdate(index, { label: value })}
            />
            <ComboField
                label={t(TK.fieldType)}
                value={String(field.type)}
                options={fieldTypeOptions}
                onChange={value => value !== undefined && onUpdate(index, { type: parseInt(value, 10) })}
            />
            <CheckInputField
                label={t(TK.mandatory)}
                value={field.isMandatory}
                onChange={value => onUpdate(index, { isMandatory: value })}
            />
            {field.type === SurveyFieldType.MultiOptions && (
                <TextInputField
                    label={t(TK.fieldOptions)}
                    value={optionsRaw}
                    onChange={handleOptionsChange}
                    helpText={t(TK.fieldOptions)}
                />
            )}
            {field.type === SurveyFieldType.Scale && (
                <ScaleRangeRow>
                    <NumberInputField
                        label={t(TK.scaleRange) + ' (min)'}
                        value={field.scaleMin ?? 1}
                        onChange={value => onUpdate(index, { scaleMin: value ?? 1 })}
                    />
                    <NumberInputField
                        label={t(TK.scaleRange) + ' (max)'}
                        value={field.scaleMax ?? 10}
                        onChange={value => onUpdate(index, { scaleMax: value ?? 10 })}
                    />
                </ScaleRangeRow>
            )}
        </FieldRow>
    );
};

interface SurveyFieldsEditorProps {
    fields: Array<SurveyFieldDefinitionData & { _key: number }>;
    onAddField: () => void;
    onRemoveField: (index: number) => void;
    onUpdateField: (index: number, updates: Partial<SurveyFieldDefinitionData>) => void;
}

const SurveyFieldsEditor: FC<SurveyFieldsEditorProps> = ({ fields, onAddField, onRemoveField, onUpdateField }) => {
    const t = useTranslations();

    return (
        <>
            {fields.length === 0 ? (
                <EmptyState>{t(TK.surveyNoFields)}</EmptyState>
            ) : (
                fields.map((field, index) => (
                    <FieldEditor
                        key={field._key}
                        field={field}
                        index={index}
                        onUpdate={onUpdateField}
                        onRemove={onRemoveField}
                    />
                ))
            )}
            <div style={{ display: 'flex', gap: 'var(--spacer-S)', alignItems: 'center' }}>
                <AppButton color="secondary" outline size="sm" onClick={onAddField}>
                    <AddCircleIcon style={{ fontSize: '1rem', marginRight: 'var(--spacer-XXS)' }} />
                    {t(TK.addQuestion)}
                </AppButton>
            </div>
        </>
    );
};

// ─── Channel Selector ─────────────────────────────────────────────────────────

type ChannelType = 'email' | 'whatsapp';

interface ChannelSelectorProps {
    channel: ChannelType;
    onChannelChange: (channel: ChannelType) => void;
    emailContent: React.ReactNode;
    whatsAppContent: React.ReactNode;
}

const ChannelSelector: FC<ChannelSelectorProps> = ({ channel, onChannelChange, emailContent, whatsAppContent }) => {
    const t = useTranslations();

    return (
        <>
            {(['email', 'whatsapp'] as const).map(ch => {
                const isSelected = channel === ch;
                return (
                    <ChannelCard key={ch} $isActive={isSelected} $isSelected={isSelected}>
                        <ChannelHeader
                            $isActive={isSelected}
                            $isSelected={isSelected}
                            onClick={() => onChannelChange(ch)}
                        >
                            <CheckInputField
                                label=""
                                value={isSelected}
                                onChange={() => onChannelChange(ch)}
                                onClick={(e: React.MouseEvent) => e.stopPropagation()}
                            />
                            <ChannelInfo>
                                {ch === 'email'
                                    ? <EmailIcon style={{ fontSize: '1.2rem', verticalAlign: 'middle' }} />
                                    : <WhatsAppIcon style={{ fontSize: '1.2rem', verticalAlign: 'middle', color: '#25D366' }} />
                                }
                                <b>{ch === 'email' ? t(TK.surveyChannelEmail) : t(TK.surveyChannelWhatsApp)}</b>
                                <ChannelStatus $isActive={isSelected}>
                                    {isSelected ? t(TK.active) : t(TK.inactive)}
                                </ChannelStatus>
                            </ChannelInfo>
                        </ChannelHeader>
                        {isSelected && (
                            <ChannelContent>
                                {ch === 'email' ? emailContent : whatsAppContent}
                            </ChannelContent>
                        )}
                    </ChannelCard>
                );
            })}
        </>
    );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const SurveysSettings: FC = () => {
    const t = useTranslations();
    const dispatch = useDispatch();
    const generalSettings = useGeneralSettings();
    const serviceTypeOptions = useMemo(() => Object.keys(generalSettings.services ?? {}), [generalSettings.services]);
    const remindersSettings = useSelector((state: ApplicationState) => state.settings.reminders);
    const surveys = useSelector(getSurveys) ?? [];
    const loading = useSelector(isSurveysLoading);
    const clients = useSelector(getEntitiesDataItems<ClientEntity>('clients'));
    const clientSearchConfig = useMemo(() => createClientSearchConfig(clients), [clients]);
    const companyName = generalSettings.companyName || '';

    const [saving, setSaving] = useState(false);
    const aiPoweredRequests = useAIPoweredRequests();
    const surveyGenerationQuota = aiPoweredRequests?.surveys?.totalAvailableQuota ?? 5;
    type SurveyDraftField = SurveyFieldDefinitionData & { _key: number };
    type SurveyDraftForm = Omit<SurveyDraftData, 'fields'> & { fields: SurveyDraftField[] };
    type SurveyEditForm = Omit<SurveyUpdateRequest, 'fields'> & { fields: SurveyDraftField[] };

    const defaultEmailSubject = useMemo(() => t(TK.surveyEmailDefaultSubject), [t]);
    const defaultEmailHtml = useMemo(
        () => t(TK.surveyEmailDefaultBody).replace(/\{company-name\}/g, companyName),
        [t, companyName],
    );

    // Create
    const [showCreate, setShowCreate] = useState(false);
    const [createChannel, setCreateChannel] = useState<ChannelType>('email');
    const [createQuestionsOpen, setCreateQuestionsOpen] = useState(true);
    const [createMode, setCreateMode] = useState<'manual' | 'ai' | null>(null);
    const [generationPrompt, setGenerationPrompt] = useState('');
    const [generating, setGenerating] = useState(false);
    const [aiRefineOpen, setAiRefineOpen] = useState(false);
    const [aiGenerated, setAiGenerated] = useState(false);
    const [createForm, setCreateForm] = useState<SurveyDraftForm>(() => ({
        name: '',
        description: '',
        emailSubject: '',
        emailHtml: '',
        emailFromName: '',
        emailFrom: '',
        fields: [],
        whatsAppEnabled: false,
    }));

    // Edit
    const [editingId, setEditingId] = useState<string | undefined>(undefined);
    const [editChannel, setEditChannel] = useState<ChannelType>('email');
    const [editForm, setEditForm] = useState<SurveyEditForm>({
        name: '',
        description: '',
        emailSubject: '',
        emailHtml: '',
        emailFromName: '',
        emailFrom: '',
        fields: [],
        whatsAppEnabled: false,
    });

    // Delete
    const [deletingId, setDeletingId] = useState<string | undefined>(undefined);

    // Share
    const [sharingSurvey, setSharingSurvey] = useState<SurveySettingsData | undefined>(undefined);
    const [shareLink, setShareLink] = useState('');
    const [shareLinkLoading, setShareLinkLoading] = useState(false);
    const [contactClientOption, setContactClientOption] = useState<string>('');
    const [contactName, setContactName] = useState('');
    const [contactEmail, setContactEmail] = useState('');
    const [contactPhone, setContactPhone] = useState('');
    const [contactChannel, setContactChannel] = useState<ChannelType>('email');
    const [sendingContact, setSendingContact] = useState(false);

    const loadSurveys = useCallback(() => {
        dispatch(loadSurveysIfNeeded({ silent: false }));
    }, [dispatch]);

    useEffect(() => {
        loadSurveys();
    }, [loadSurveys]);

    const resetCreateForm = useCallback(() => {
        setCreateChannel('email');
        setCreateQuestionsOpen(true);
        setGenerationPrompt('');
        setAiRefineOpen(false);
        setAiGenerated(false);
        setCreateForm({
            name: '',
            description: '',
            emailSubject: defaultEmailSubject,
            emailHtml: defaultEmailHtml,
            emailFromName: '',
            emailFrom: '',
            fields: [],
            whatsAppEnabled: false,
        });
    }, [defaultEmailHtml, defaultEmailSubject]);

    const openCreate = useCallback((mode: 'manual' | 'ai') => {
        resetCreateForm();
        setCreateMode(mode);
        setShowCreate(true);
    }, [resetCreateForm]);

    const closeCreate = useCallback(() => {
        setShowCreate(false);
        setCreateMode(null);
        resetCreateForm();
    }, [resetCreateForm]);

    const openSurveyPreview = useCallback(async (surveyId: string, previewWindow?: Window | null) => {
        try {
            const link = await getSurveyPreviewLinkAsync(surveyId);

            if (previewWindow && !previewWindow.closed) {
                previewWindow.location.href = link;
                return;
            }

            window.open(link, '_blank');
        } catch {
            if (previewWindow && !previewWindow.closed) {
                previewWindow.close();
            }

            dispatch(alertError(t(TK.somethingWentWrong)));
        }
    }, [dispatch, t]);

    const openEdit = useCallback((survey: SurveySettingsData) => {
        setEditingId(survey.id);
        setEditChannel(survey.whatsAppEnabled ? 'whatsapp' : 'email');
        setEditForm({
            name: survey.name,
            description: survey.description ?? '',
            emailSubject: survey.emailSubject ?? '',
            emailHtml: survey.emailHtml ?? '',
            emailFromName: survey.emailFromName ?? '',
            emailFrom: survey.emailFrom ?? '',
            automaticForServiceTypeIds: survey.automaticForServiceTypeIds ?? [],
            isDefaultAutomaticSurvey: survey.isDefaultAutomaticSurvey ?? false,
            fields: toDraftFields(survey.fields ?? []),
            whatsAppEnabled: survey.whatsAppEnabled ?? false,
            autoCreateClientIfNotExists: survey.autoCreateClientIfNotExists ?? false,
        });
    }, []);

    const openShare = useCallback(async (survey: SurveySettingsData) => {
        setSharingSurvey(survey);
        setShareLink('');
        setContactClientOption('');
        setContactName('');
        setContactEmail('');
        setContactPhone('');
        setContactChannel(survey.whatsAppEnabled === true ? 'whatsapp' : 'email');
        setShareLinkLoading(true);
        try {
            const link = await getSurveyShareLinkAsync(survey.id);
            setShareLink(link);
        } catch (err: unknown) {
            dispatch(alertError(getErrorMessage(err) || t(TK.somethingWentWrong)));
        } finally {
            setShareLinkLoading(false);
        }
    }, [dispatch, t]);

    const handleCopyShareLink = useCallback(async () => {
        if (!shareLink) return;
        try {
            await navigator.clipboard.writeText(shareLink);
            dispatch(alertSuccess(t(TK.linkCopied)));
        } catch {
            dispatch(alertError(t(TK.somethingWentWrong)));
        }
    }, [dispatch, shareLink, t]);

    const handleSendToContact = useCallback(async () => {
        if (!sharingSurvey) return;
        const useWhatsApp = contactChannel === 'whatsapp';
        if (useWhatsApp ? !contactPhone.trim() : !contactEmail.trim()) {
            dispatch(alertError(t(useWhatsApp ? TK.contactPhone : TK.contactEmail) + ' ' + t(TK.mandatory).toLowerCase()));
            return;
        }
        setSendingContact(true);
        try {
            await sendSurveyToContactAsync(sharingSurvey.id, {
                channel: contactChannel,
                name: contactName.trim() || undefined,
                email: contactEmail.trim() || undefined,
                phoneNumber: contactPhone.trim() || undefined,
            });
            dispatch(alertSuccess(t(TK.entityCreatedSuccessfully)));
            setContactClientOption('');
            setContactName('');
            setContactEmail('');
            setContactPhone('');
            dispatch(refreshSurveys({ silent: true }));
        } catch (err: unknown) {
            dispatch(alertError(getErrorMessage(err) || t(TK.somethingWentWrong)));
        } finally {
            setSendingContact(false);
        }
    }, [contactChannel, contactEmail, contactName, contactPhone, dispatch, sharingSurvey, t]);

    const handleContactClientChange = useCallback((option: string) => {
        setContactClientOption(option);
        const client = clientSearchConfig.getClientByOption(option);
        if (client) {
            setContactName(client.name ?? '');
            setContactEmail(client.email ?? '');
            setContactPhone(client.phoneNumbers?.[0] ?? '');
        }
    }, [clientSearchConfig]);

    const handleGenerateSurvey = useCallback(async () => {
        if (!generationPrompt.trim() || generating) return;

        setGenerating(true);
        try {
            const result = await generateSurveyAsync({
                prompt: generationPrompt,
                template: {
                    ...createForm,
                    fields: stripDraftFields(createForm.fields),
                },
            });

            const generatedFields = (result.fields ?? []).map(field => ({
                ...field,
                _key: ++_fieldIdCounter,
            }));
            const nextWhatsAppEnabled = result.whatsAppEnabled ?? createForm.whatsAppEnabled ?? false;

            setCreateForm(prev => ({
                ...prev,
                name: result.name?.trim() || prev.name,
                description: result.description?.trim() || prev.description,
                emailSubject: result.emailSubject?.trim() || prev.emailSubject,
                emailHtml: result.emailHtml?.trim() || prev.emailHtml,
                emailFromName: result.emailFromName?.trim() || prev.emailFromName,
                emailFrom: result.emailFrom?.trim() || prev.emailFrom,
                automaticForServiceTypeIds: result.automaticForServiceTypeIds?.length ? result.automaticForServiceTypeIds : prev.automaticForServiceTypeIds,
                isDefaultAutomaticSurvey: result.isDefaultAutomaticSurvey ?? prev.isDefaultAutomaticSurvey,
                whatsAppEnabled: nextWhatsAppEnabled,
                autoCreateClientIfNotExists: result.autoCreateClientIfNotExists ?? prev.autoCreateClientIfNotExists,
                fields: generatedFields.length > 0 ? generatedFields : prev.fields,
            }));
            setCreateChannel(nextWhatsAppEnabled ? 'whatsapp' : 'email');

            setAiGenerated(true);
            if (aiRefineOpen) {
                setAiRefineOpen(false);
                setGenerationPrompt('');
            }
        } catch (err: unknown) {
            dispatch(alertError(getErrorMessage(err) || t(TK.somethingWentWrong)));
        } finally {
            setGenerating(false);
        }
    }, [aiRefineOpen, createForm, dispatch, generating, generationPrompt, t]);

    const handleCreate = useCallback(async () => {
        if (!createForm.name.trim()) {
            dispatch(alertError(`${t(TK.name)} ${t(TK.mandatory).toLowerCase()}`));
            return;
        }
        if (createForm.fields.length === 0) {
            dispatch(alertError(t(TK.surveyNoFields)));
            return;
        }

        const formToSave: SurveyDraftData = {
            ...createForm,
            fields: stripDraftFields(createForm.fields),
            whatsAppEnabled: createChannel === 'whatsapp',
        };
        const previewWindow = window.open('', '_blank');

        setSaving(true);
        try {
            const createdSurveyId = await createSurveyAsync(formToSave);
            dispatch(alertSuccess(t(TK.entityCreatedSuccessfully)));
            closeCreate();
            await openSurveyPreview(createdSurveyId, previewWindow);
            dispatch(refreshSurveys({ silent: false }));
        } catch (err: unknown) {
            if (previewWindow && !previewWindow.closed) {
                previewWindow.close();
            }
            dispatch(alertError(getErrorMessage(err) || t(TK.somethingWentWrong)));
        } finally {
            setSaving(false);
        }
    }, [closeCreate, createForm, createChannel, dispatch, openSurveyPreview, t]);

    const handleUpdate = useCallback(async () => {
        if (!editingId) return;
        if (!editForm.name.trim()) {
            dispatch(alertError(`${t(TK.name)} ${t(TK.mandatory).toLowerCase()}`));
            return;
        }
        if (editForm.fields.length === 0) {
            dispatch(alertError(t(TK.surveyNoFields)));
            return;
        }

        const formToSave: SurveyUpdateRequest = {
            ...editForm,
            fields: stripDraftFields(editForm.fields),
            whatsAppEnabled: editChannel === 'whatsapp',
        };
        const previewWindow = window.open('', '_blank');

        setSaving(true);
        try {
            await updateSurveyAsync(editingId, formToSave);
            dispatch(alertSuccess(t(TK.savedSuccessfully)));
            setEditingId(undefined);
            await openSurveyPreview(editingId, previewWindow);
            dispatch(refreshSurveys({ silent: false }));
        } catch (err: unknown) {
            if (previewWindow && !previewWindow.closed) {
                previewWindow.close();
            }
            dispatch(alertError(getErrorMessage(err) || t(TK.somethingWentWrong)));
        } finally {
            setSaving(false);
        }
    }, [editingId, editForm, editChannel, dispatch, openSurveyPreview, t]);

    const handleDelete = useCallback(async () => {
        if (!deletingId) return;
        try {
            await deleteSurveyAsync(deletingId);
            dispatch(alertSuccess(t(TK.entityDeletedSuccessfully)));
            setDeletingId(undefined);
            dispatch(refreshSurveys({ silent: false }));
        } catch (err: unknown) {
            dispatch(alertError(getErrorMessage(err) || t(TK.somethingWentWrong)));
        }
    }, [deletingId, dispatch, t]);

    const addCreateField = useCallback(() => {
        setCreateForm(prev => ({ ...prev, fields: [...prev.fields, createEmptyField()] }));
    }, []);

    const removeCreateField = useCallback((index: number) => {
        setCreateForm(prev => ({ ...prev, fields: prev.fields.filter((_, i) => i !== index) }));
    }, []);

    const updateCreateField = useCallback((index: number, updates: Partial<SurveyFieldDefinitionData>) => {
        setCreateForm(prev => ({
            ...prev,
            fields: prev.fields.map((f, i) => i === index ? { ...f, ...updates } : f),
        }));
    }, []);

    const addEditField = useCallback(() => {
        setEditForm(prev => ({ ...prev, fields: [...prev.fields, createEmptyField()] }));
    }, []);

    const removeEditField = useCallback((index: number) => {
        setEditForm(prev => ({ ...prev, fields: prev.fields.filter((_, i) => i !== index) }));
    }, []);

    const updateEditField = useCallback((index: number, updates: Partial<SurveyFieldDefinitionData>) => {
        setEditForm(prev => ({
            ...prev,
            fields: prev.fields.map((f, i) => i === index ? { ...f, ...updates } : f),
        }));
    }, []);

    const createEmailContent = (
        <>
            <TextInputField
                label={t(TK.fromName)}
                value={createForm.emailFromName || remindersSettings.emailName || companyName}
                onChange={value => setCreateForm(prev => ({ ...prev, emailFromName: value }))}
            />
            <TextInputField
                label={t(TK.fromEmail)}
                helpText={t(TK.fromEmailWarningMessage)}
                placeholder={t(TK.fromEmailWarningMessage)}
                value={createForm.emailFrom || remindersSettings.emailFrom || ''}
                onChange={emailFrom => /^[a-z0-9._+\-@]*$/.test(emailFrom) && setCreateForm(prev => ({ ...prev, emailFrom }))}
            />
            <TextInputField
                label={t(TK.subject)}
                value={createForm.emailSubject}
                onChange={value => setCreateForm(prev => ({ ...prev, emailSubject: value }))}
            />
            <TextInputField
                label={t(TK.emailBody)}
                helpText={t(TK.surveyEmailBodyHelp)}
                type="textarea"
                rows={6}
                value={htmlToPlainText(createForm.emailHtml)}
                onChange={value => setCreateForm(prev => ({ ...prev, emailHtml: plainTextToHtml(value) }))}
            />
        </>
    );

    const createWhatsAppContent = (
        <TextInputField
            label={t(TK.message)}
            type="textarea"
            rows={4}
            value={t(TK.surveyWhatsAppDefaultMessage)}
            disabled
        />
    );

    const editEmailContent = (
        <>
            <TextInputField
                label={t(TK.fromName)}
                value={editForm.emailFromName || remindersSettings.emailName || companyName}
                onChange={value => setEditForm(prev => ({ ...prev, emailFromName: value }))}
            />
            <TextInputField
                label={t(TK.fromEmail)}
                helpText={t(TK.fromEmailWarningMessage)}
                placeholder={t(TK.fromEmailWarningMessage)}
                value={editForm.emailFrom || remindersSettings.emailFrom || ''}
                onChange={emailFrom => /^[a-z0-9._+\-@]*$/.test(emailFrom) && setEditForm(prev => ({ ...prev, emailFrom }))}
            />
            <TextInputField
                label={t(TK.subject)}
                value={editForm.emailSubject}
                onChange={value => setEditForm(prev => ({ ...prev, emailSubject: value }))}
            />
            <TextInputField
                label={t(TK.emailBody)}
                helpText={t(TK.surveyEmailBodyHelp)}
                type="textarea"
                rows={6}
                value={htmlToPlainText(editForm.emailHtml)}
                onChange={value => setEditForm(prev => ({ ...prev, emailHtml: plainTextToHtml(value) }))}
            />
        </>
    );

    const editWhatsAppContent = (
        <TextInputField
            label={t(TK.message)}
            type="textarea"
            rows={4}
            value={t(TK.surveyWhatsAppDefaultMessage)}
            disabled
        />
    );

    return (
        <>
            <Alert color="secondary" isOpen style={{ whiteSpace: 'pre-line' }}>
                {t(TK.surveysFeatureDescription)}
            </Alert>
            <ListGroup style={{ marginBottom: 'var(--spacer-S)' }}>
                {loading ? (
                    <ListGroupItem><i>{t(TK.loading)}...</i></ListGroupItem>
                ) : surveys.length === 0 ? (
                    <ListGroupItem><EmptyState>{t(TK.noSurveys)}</EmptyState></ListGroupItem>
                ) : surveys.map(survey => (
                    <SurveyCard key={survey.id}>
                        <SurveyMeta>
                            <SurveyName>{survey.name}</SurveyName>
                            <SurveyDate>{survey.creationDate?.substring(0, 10)}</SurveyDate>
                            <SurveyFieldBadges>
                                {survey.fields.map((f, i) => (
                                    <Badge key={i} color="secondary" style={{ fontSize: '0.72em' }}>
                                        {f.label}
                                    </Badge>
                                ))}
                            </SurveyFieldBadges>
                            <SurveyStats>
                                <SurveyStatChip>{t(TK.surveySentCount)}: {survey.totalSent ?? 0}</SurveyStatChip>
                                <SurveyStatChip>{t(TK.surveyResponseCount)}: {survey.totalResponses ?? 0}</SurveyStatChip>
                            </SurveyStats>
                        </SurveyMeta>
                        <SurveyActions>
                            <IconButton
                                size="small"
                                title={t(TK.surveyPreview)}
                                onClick={() => openSurveyPreview(survey.id)}
                            >
                                <VisibilityIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                                size="small"
                                title={t(TK.shareSurvey)}
                                onClick={() => openShare(survey)}
                            >
                                <ShareIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                                size="small"
                                title={t(TK.surveys)}
                                onClick={() => dispatch(push(`${paths.analytics()}?tab=surveys&surveyId=${survey.id}`))}
                            >
                                <BarChartIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" title={t(TK.editSurvey)} onClick={() => openEdit(survey)}>
                                <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                                size="small"
                                title={t(TK.delete)}
                                onClick={() => setDeletingId(survey.id)}
                                style={{ color: 'var(--danger-color)' }}
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </SurveyActions>
                    </SurveyCard>
                ))}
                <AddSurveyRow>
                    <div style={{ display: 'flex', gap: 'var(--spacer-S)', flexWrap: 'wrap' }}>
                        <CreateButton id="create-survey-manual-button" onClick={() => openCreate('manual')}>
                            <EditIcon style={{ fontSize: '1rem' }} />
                            {t(TK.createManually)}
                        </CreateButton>
                        <CreateButton id="create-survey-ai-button" $ai onClick={() => openCreate('ai')}>
                            <SparkleAIIcon style={{ fontSize: '1rem' }} />
                            {t(TK.createWithAI)}
                        </CreateButton>
                    </div>
                </AddSurveyRow>
            </ListGroup>

            {/* ── Create Modal ── */}
            <Modal isOpen={showCreate} toggle={closeCreate} size="lg">
                <ModalHeader toggle={closeCreate}>
                    {t(TK.surveyBuilder)}
                </ModalHeader>
                <ModalBody>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacer-S)' }}>
                        {(createMode !== 'ai' || aiGenerated) && (
                            <>
                                <TextInputField
                                    label={`${t(TK.name)} *`}
                                    value={createForm.name}
                                    onChange={value => setCreateForm(prev => ({ ...prev, name: value }))}
                                />
                                <SurveyDescriptionEditor
                                    label={t(TK.surveyDescription)}
                                    placeholder={t(TK.surveyDescriptionPlaceholder)}
                                    value={createForm.description ?? ''}
                                    onChange={value => setCreateForm(prev => ({ ...prev, description: value }))}
                                />
                            </>
                        )}

                        {createMode === 'ai' && (
                            <AISection>
                                <TextInputField
                                    label={t(TK.surveyGenerationPrompt)}
                                    helpText={t(TK.surveyGenerationPromptHelp)}
                                    type="textarea"
                                    rows={4}
                                    value={generationPrompt}
                                    onChange={setGenerationPrompt}
                                />
                                <Alert color="secondary" style={{ marginBottom: 0 }}>
                                    {t(TK.surveyGenerationQuotaInfo, surveyGenerationQuota.toString())}
                                </Alert>
                                {generating ? (
                                    <GeneratingOverlay>
                                        <CircularProgress size={48} style={{ color: 'var(--info-color)' }} />
                                        <GeneratingLabel>{t(TK.loading)}…</GeneratingLabel>
                                    </GeneratingOverlay>
                                ) : (
                                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                        <AppButton
                                            type="button"
                                            color="primary"
                                            disabled={!generationPrompt.trim()}
                                            onClick={handleGenerateSurvey}
                                            style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacer-XS)' }}
                                        >
                                            <SparkleAIIcon style={{ fontSize: '1.1rem' }} />
                                            {createForm.fields.length ? t(TK.regenerateSurvey) : t(TK.generateSurvey)}
                                        </AppButton>
                                    </div>
                                )}
                            </AISection>
                        )}

                        {createMode !== null && (createMode !== 'ai' || aiGenerated) && (
                        <>
                        <CollapsableItem size="sm" title={t(TK.surveyChannelSection)}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacer-S)', paddingBottom: 'var(--spacer-S)' }}>
                                <ChannelSelector
                                    channel={createChannel}
                                    onChannelChange={setCreateChannel}
                                    emailContent={createEmailContent}
                                    whatsAppContent={createWhatsAppContent}
                                />
                            </div>
                        </CollapsableItem>

                        <CollapsableItem size="sm" title={t(TK.surveyConfigurationSection)}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacer-S)', paddingBottom: 'var(--spacer-S)' }}>
                                <CheckInputField
                                    label={t(TK.surveyIsDefaultAutomaticSurvey)}
                                    helpText={t(TK.surveyIsDefaultAutomaticSurveyHelp)}
                                    value={createForm.isDefaultAutomaticSurvey ?? false}
                                    onChange={value => setCreateForm(prev => ({ ...prev, isDefaultAutomaticSurvey: value }))}
                                />
                                {serviceTypeOptions.length > 0 && (
                                    <AutocompleteTextField
                                        label={t(TK.surveyAutomaticServiceTypes)}
                                        helpText={t(TK.surveyAutomaticServiceTypesHelp)}
                                        multiSelect
                                        options={serviceTypeOptions}
                                        values={createForm.automaticForServiceTypeIds ?? []}
                                        onValuesChanged={automaticForServiceTypeIds => setCreateForm(prev => ({ ...prev, automaticForServiceTypeIds }))}
                                    />
                                )}
                                <CheckInputField
                                    label={t(TK.surveyAutoCreateClient)}
                                    helpText={t(TK.surveyAutoCreateClientHelp)}
                                    value={createForm.autoCreateClientIfNotExists ?? false}
                                    onChange={value => setCreateForm(prev => ({ ...prev, autoCreateClientIfNotExists: value }))}
                                />
                            </div>
                        </CollapsableItem>

                        <CollapsableItem size="sm" title={t(TK.surveyQuestions)} isOpen={createQuestionsOpen} onSetOpen={setCreateQuestionsOpen}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacer-S)', paddingBottom: 'var(--spacer-S)' }}>
                                <SurveyFieldsEditor
                                    fields={createForm.fields}
                                    onAddField={addCreateField}
                                    onRemoveField={removeCreateField}
                                    onUpdateField={updateCreateField}
                                />
                                {aiRefineOpen && createMode !== 'ai' && (
                                    <AISection>
                                        <TextInputField
                                            label={t(TK.surveyGenerationPrompt)}
                                            helpText={t(TK.surveyGenerationPromptHelp)}
                                            type="textarea"
                                            rows={4}
                                            value={generationPrompt}
                                            onChange={setGenerationPrompt}
                                        />
                                        <Alert color="secondary" style={{ marginBottom: 0 }}>
                                            {t(TK.surveyGenerationQuotaInfo, surveyGenerationQuota.toString())}
                                        </Alert>
                                        {generating ? (
                                            <GeneratingOverlay>
                                                <CircularProgress size={48} style={{ color: 'var(--info-color)' }} />
                                                <GeneratingLabel>{t(TK.loading)}…</GeneratingLabel>
                                            </GeneratingOverlay>
                                        ) : (
                                            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                                <AppButton
                                                    type="button"
                                                    color="primary"
                                                    disabled={!generationPrompt.trim()}
                                                    onClick={handleGenerateSurvey}
                                                    style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacer-XS)' }}
                                                >
                                                    <SparkleAIIcon style={{ fontSize: '1.1rem' }} />
                                                    {t(TK.regenerateSurvey)}
                                                </AppButton>
                                            </div>
                                        )}
                                    </AISection>
                                )}
                                <div style={{ display: 'flex', gap: 'var(--spacer-S)', alignItems: 'center' }}>
                                    {createMode !== 'ai' && createForm.fields.length > 0 && (
                                        <AppButton
                                            type="button"
                                            color={aiRefineOpen ? 'info' : 'secondary'}
                                            outline={!aiRefineOpen}
                                            size="sm"
                                            onClick={() => setAiRefineOpen(o => !o)}
                                            style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacer-XS)' }}
                                        >
                                            <SparkleAIIcon style={{ fontSize: '1rem' }} />
                                            {t(TK.refineWithAI)}
                                        </AppButton>
                                    )}
                                </div>
                            </div>
                        </CollapsableItem>
                        </>
                        )}
                    </div>
                </ModalBody>
                <ModalFooter style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--spacer-S)' }}>
                    <span style={{ color: 'var(--gray-600)', fontSize: '0.9rem' }}>
                        (*) {t(TK.surveySaveToPreviewDisclaimer)}
                    </span>
                    <div style={{ display: 'flex', gap: 'var(--spacer-S)' }}>
                        <AppButton color="secondary" onClick={closeCreate}>
                            {t(TK.cancel)}
                        </AppButton>
                        <AppButton color="success" onClick={handleCreate} disabled={saving || createMode === null}>
                            {saving ? t(TK.saving) : t(TK.save)}
                        </AppButton>
                    </div>
                </ModalFooter>
            </Modal>

            {/* ── Edit Modal ── */}
            <Modal isOpen={!!editingId} toggle={() => setEditingId(undefined)} size="lg">
                <ModalHeader toggle={() => setEditingId(undefined)}>
                    {t(TK.editSurvey)}
                </ModalHeader>
                <ModalBody>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacer-S)' }}>
                        <TextInputField
                            label={`${t(TK.name)} *`}
                            value={editForm.name}
                            onChange={value => setEditForm(prev => ({ ...prev, name: value }))}
                        />
                        <SurveyDescriptionEditor
                            label={t(TK.surveyDescription)}
                            placeholder={t(TK.surveyDescriptionPlaceholder)}
                            value={editForm.description ?? ''}
                            onChange={value => setEditForm(prev => ({ ...prev, description: value }))}
                        />

                        <CollapsableItem size="sm" title={t(TK.surveyChannelSection)}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacer-S)', paddingBottom: 'var(--spacer-S)' }}>
                                <ChannelSelector
                                    channel={editChannel}
                                    onChannelChange={setEditChannel}
                                    emailContent={editEmailContent}
                                    whatsAppContent={editWhatsAppContent}
                                />
                            </div>
                        </CollapsableItem>

                        <CollapsableItem size="sm" title={t(TK.surveyConfigurationSection)}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacer-S)', paddingBottom: 'var(--spacer-S)' }}>
                                <CheckInputField
                                    label={t(TK.surveyIsDefaultAutomaticSurvey)}
                                    helpText={t(TK.surveyIsDefaultAutomaticSurveyHelp)}
                                    value={editForm.isDefaultAutomaticSurvey ?? false}
                                    onChange={value => setEditForm(prev => ({ ...prev, isDefaultAutomaticSurvey: value }))}
                                />
                                {serviceTypeOptions.length > 0 && (
                                    <AutocompleteTextField
                                        label={t(TK.surveyAutomaticServiceTypes)}
                                        multiSelect
                                        options={serviceTypeOptions}
                                        values={editForm.automaticForServiceTypeIds ?? []}
                                        onValuesChanged={automaticForServiceTypeIds => setEditForm(prev => ({ ...prev, automaticForServiceTypeIds }))}
                                    />
                                )}
                                <CheckInputField
                                    label={t(TK.surveyAutoCreateClient)}
                                    helpText={t(TK.surveyAutoCreateClientHelp)}
                                    value={editForm.autoCreateClientIfNotExists ?? false}
                                    onChange={value => setEditForm(prev => ({ ...prev, autoCreateClientIfNotExists: value }))}
                                />
                            </div>
                        </CollapsableItem>

                        <CollapsableItem size="sm" title={t(TK.surveyQuestions)}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacer-S)', paddingBottom: 'var(--spacer-S)' }}>
                                <SurveyFieldsEditor
                                    fields={editForm.fields}
                                    onAddField={addEditField}
                                    onRemoveField={removeEditField}
                                    onUpdateField={updateEditField}
                                />
                            </div>
                        </CollapsableItem>
                    </div>
                </ModalBody>
                <ModalFooter style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--spacer-S)' }}>
                    <span style={{ color: 'var(--gray-600)', fontSize: '0.9rem' }}>
                        (*) {t(TK.surveySaveToPreviewDisclaimer)}
                    </span>
                    <div style={{ display: 'flex', gap: 'var(--spacer-S)' }}>
                        <AppButton color="secondary" onClick={() => setEditingId(undefined)}>
                            {t(TK.cancel)}
                        </AppButton>
                        <AppButton color="success" onClick={handleUpdate} disabled={saving}>
                            {saving ? t(TK.saving) : t(TK.save)}
                        </AppButton>
                    </div>
                </ModalFooter>
            </Modal>

            {/* ── Share Modal ── */}
            <Modal isOpen={!!sharingSurvey} toggle={() => setSharingSurvey(undefined)} size="lg">
                <ModalHeader toggle={() => setSharingSurvey(undefined)}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacer-S)' }}>
                        <ShareIconWrapper><ShareIcon /></ShareIconWrapper>
                        <div>
                            <div>{t(TK.shareSurvey)}</div>
                            {sharingSurvey && <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', fontWeight: 400 }}>{sharingSurvey.name}</div>}
                        </div>
                    </div>
                </ModalHeader>
                <ModalBody>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacer-S)' }}>
                        <Alert color="secondary" style={{ marginBottom: 0, whiteSpace: 'pre-line' }}>
                            {t(TK.shareSurveyHelp)}
                        </Alert>

                        <ShareLinkRow>
                            <ShareLinkText>
                                {shareLinkLoading ? `${t(TK.loading)}…` : shareLink}
                            </ShareLinkText>
                            <AppButton color="primary" size="sm" onClick={handleCopyShareLink} disabled={!shareLink || shareLinkLoading}>
                                {t(TK.copyLink)}
                            </AppButton>
                        </ShareLinkRow>

                        <ShareSectionDivider>{t(TK.sendToContact)}</ShareSectionDivider>

                        <Alert color="secondary" style={{ marginBottom: 0, whiteSpace: 'pre-line' }}>
                            {t(TK.sendToContactHelp)}
                        </Alert>
                        <AutocompleteTextField
                            label={t(TK.contactName)}
                            value={contactClientOption || contactName}
                            options={clientSearchConfig.options}
                            optionLabelResolver={clientSearchConfig.optionLabelResolver}
                            searchTextResolver={clientSearchConfig.searchTextResolver}
                            searchMatcher={clientSearchConfig.searchMatcher}
                            onValueChanged={option => handleContactClientChange(option)}
                            onTextChanged={text => {
                                if (!clientSearchConfig.getClientByOption(contactClientOption)) {
                                    setContactName(text);
                                }
                            }}
                        />
                        <ComboField
                            label={t(TK.surveyChannelSection)}
                            value={contactChannel}
                            options={[
                                { value: 'email', label: t(TK.surveyChannelEmail), icon: <EmailIcon fontSize="small" /> },
                                { value: 'whatsapp', label: t(TK.surveyChannelWhatsApp), icon: <WhatsAppIcon fontSize="small" /> },
                            ]}
                            onChange={value => setContactChannel(value === 'whatsapp' ? 'whatsapp' : 'email')}
                        />
                        {contactChannel === 'whatsapp'
                            ? <TextInputField
                                label={t(TK.contactPhone)}
                                value={contactPhone}
                                onChange={setContactPhone}
                            />
                            : <TextInputField
                                label={t(TK.contactEmail)}
                                value={contactEmail}
                                onChange={setContactEmail}
                            />
                        }
                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <AppButton
                                color="success"
                                onClick={handleSendToContact}
                                disabled={sendingContact || (contactChannel === 'whatsapp' ? !contactPhone.trim() : !contactEmail.trim())}
                            >
                                {sendingContact
                                    ? t(TK.saving)
                                    : (contactChannel === 'whatsapp'
                                        ? <><WhatsAppIcon fontSize="small" style={{ verticalAlign: 'middle', marginRight: 'var(--spacer-XXS)' }} />{t(TK.sendByWhatsApp)}</>
                                        : <><EmailIcon fontSize="small" style={{ verticalAlign: 'middle', marginRight: 'var(--spacer-XXS)' }} />{t(TK.sendByEmail)}</>)
                                }
                            </AppButton>
                        </div>
                    </div>
                </ModalBody>
                <ModalFooter>
                    <AppButton color="secondary" onClick={() => setSharingSurvey(undefined)}>{t(TK.cancel)}</AppButton>
                </ModalFooter>
            </Modal>

            {/* ── Delete Confirmation Modal ── */}
            <Modal isOpen={!!deletingId} toggle={() => setDeletingId(undefined)}>
                <ModalHeader toggle={() => setDeletingId(undefined)}>{t(TK.areYouSure)}</ModalHeader>
                <ModalBody>{t(TK.youRAboutToDelete)}</ModalBody>
                <ModalFooter>
                    <AppButton color="danger" onClick={handleDelete}>{t(TK.delete)}</AppButton>{' '}
                    <AppButton color="secondary" onClick={() => setDeletingId(undefined)}>{t(TK.cancel)}</AppButton>
                </ModalFooter>
            </Modal>
        </>
    );
};

export default SurveysSettings;
